"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_REAPPLY_AFTER_MONTHS = exports.DEFAULT_REAPPLY_AFTER_MONTHS = exports.manualCandidateSchema = exports.correctSourceSchema = exports.doNotRehireSchema = exports.mergeCandidatesSchema = exports.MERGE_FIELD_LABELS = exports.MERGE_FIELDS = exports.flagActionSchema = exports.DUPLICATE_MATCH_LABELS = exports.candidateListQuerySchema = exports.recordableSourceOptions = exports.RECORDABLE_SOURCES = exports.WALK_IN_SOURCE = exports.RETENTION_DAYS_AFTER_CLOSE = exports.MERGE_REVERSIBLE_DAYS = void 0;
exports.applicationSourceLabel = applicationSourceLabel;
exports.reapplyBlockedMessage = reapplyBlockedMessage;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const publishing_1 = require("./publishing");
const schemas_1 = require("./schemas");
// S14 — candidates and applications: CAND-01, CAND-02, CAND-03, CAND-06, CAND-07;
// F7.04, F7.06–F7.08, F7.10, F7.12, F7.13. Decisions: DECISIONS-LOG S14 Q1–Q9.
//
// Responses keep candidates under a `candidate` / `candidates` key: that is how the
// serialisation layer knows a node is a candidate and masks its §L23 fields
// (non-negotiable #5). A candidate belongs to no venture (D1), so it is masked the
// same wherever it appears.
/** §L2: a merge can be undone for 30 days, then it is permanent. */
exports.MERGE_REVERSIBLE_DAYS = 30;
/** D15: the countdown after an application's terminal status. */
exports.RETENTION_DAYS_AFTER_CLOSE = 90;
// --- Sources ------------------------------------------------------------------------
/** F7.10: someone who handed over a CV in person, or on paper. */
exports.WALK_IN_SOURCE = 'walk_in';
/** What a hand entry (F7.10) or a source correction (S14 Q7) may name. */
exports.RECORDABLE_SOURCES = [exports.WALK_IN_SOURCE, ...publishing_1.PUBLISHABLE_CHANNELS, 'referral'];
/** An application's `source` in words: a channel, a walk-in, or the `direct` fallback. */
function applicationSourceLabel(source, channelName = null) {
    if (source === 'other' && channelName)
        return channelName;
    const named = (0, publishing_1.sourceLabelOverride)(source ?? publishing_1.DIRECT_SOURCE);
    if (named)
        return named;
    if (source === null || source === publishing_1.DIRECT_SOURCE)
        return publishing_1.DIRECT_SOURCE_LABEL;
    if (source === exports.WALK_IN_SOURCE)
        return 'Walk-in or paper CV';
    // S25 §L17: invited from a talent pool. Spelled here, not imported, so pools.ts can import this file.
    if (source === 'talent_pool')
        return 'Talent pool';
    // The source an upload starts with (`DEFAULT_IMPORT_SOURCE` in imports.ts, spelled here for the same reason as above).
    if (source === 'website_form')
        return 'Website form';
    if (source === 'other' && channelName)
        return channelName;
    const channel = Object.keys(publishing_1.POSTING_CHANNEL_LABELS).find((key) => key === source);
    return channel ? publishing_1.POSTING_CHANNEL_LABELS[channel] : readableSource(source);
}
/** A stored code nobody has named, in words, so no screen shows snake_case: "job_fair" reads "Job fair". */
function readableSource(source) {
    const words = source.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    return words === '' ? source : `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}
exports.recordableSourceOptions = exports.RECORDABLE_SOURCES.map((value) => ({ value, label: applicationSourceLabel(value) }));
// --- CAND-01 ------------------------------------------------------------------------
exports.candidateListQuerySchema = schemas_1.listQuerySchema;
exports.DUPLICATE_MATCH_LABELS = {
    nid: 'Same NID',
    email: 'Same email',
    phone: 'Same mobile number',
    name: 'Similar name',
    // The seed's flags (S1) name the same evidence this way.
    name_similarity: 'Similar name',
    date_of_birth: 'Same date of birth',
    employer: 'Same current employer',
    city: 'Same city',
};
exports.flagActionSchema = zod_1.z.object({ version: zod_1.z.number().int().positive() });
// --- Merge (F7.07) and unmerge (F7.08) -----------------------------------------------
/** The person-level fields §L2's field resolution covers. Money travels as one field. */
exports.MERGE_FIELDS = [
    'fullName',
    'email',
    'phone',
    'nationalId',
    'dateOfBirth',
    'gender',
    'city',
    'addressLine',
    'linkedinUrl',
    'currentEmployer',
    'currentTitle',
    'totalExperienceMonths',
    'highestEducation',
    'expectedSalary',
    'noticePeriodDays',
    'isWillingToRelocate',
];
exports.MERGE_FIELD_LABELS = {
    fullName: 'Full name',
    email: 'Email',
    phone: 'Mobile number',
    nationalId: 'NID',
    dateOfBirth: 'Date of birth',
    gender: 'Gender',
    city: 'City',
    addressLine: 'Address',
    linkedinUrl: 'LinkedIn',
    currentEmployer: 'Current employer',
    currentTitle: 'Current job title',
    totalExperienceMonths: 'Experience',
    highestEducation: 'Highest education',
    expectedSalary: 'Expected monthly salary',
    noticePeriodDays: 'Notice period',
    isWillingToRelocate: 'Willing to relocate',
};
exports.mergeCandidatesSchema = zod_1.z.object({
    /** Either record; the server decides which survives (the older). */
    candidateIds: zod_1.z.tuple([schemas_1.uuidSchema, schemas_1.uuidSchema]),
    flagId: schemas_1.uuidSchema.nullable().default(null),
    picks: zod_1.z.partialRecord(zod_1.z.enum(exports.MERGE_FIELDS), zod_1.z.enum(['survivor', 'merged'])).default({}),
    /** jobId → the application to keep, for every clash. */
    keep: zod_1.z.record(schemas_1.uuidSchema, schemas_1.uuidSchema).default({}),
    versions: zod_1.z.tuple([zod_1.z.number().int().positive(), zod_1.z.number().int().positive()]),
});
// --- F7.13 do-not-rehire ------------------------------------------------------------
exports.doNotRehireSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(3, 'Write the reason. Only HR will see it.').max(1000),
    version: zod_1.z.number().int().positive(),
});
// --- S14 Q7: correcting a "needs review" source ------------------------------------
exports.correctSourceSchema = zod_1.z.object({
    source: zod_1.z.string().trim().regex(/^[a-z][a-z0-9_]{1,59}$/, 'Pick a source from the list.'),
    version: zod_1.z.number().int().positive(),
});
// --- CAND-07 / F7.10 ----------------------------------------------------------------
const optionalText = (max) => zod_1.z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .default(null);
/**
 * A hand-entered candidate. A job is optional (E5, S14 Q2/Q5); without one the person
 * is saved alone. A CV is optional too, uploaded afterwards — paper CVs are not always
 * scanned. §L2 needs an email or a mobile number to recognise the person again.
 */
exports.manualCandidateSchema = zod_1.z
    .object({
    fullName: zod_1.z.string().trim().min(2, 'Enter the full name.').max(160),
    email: schemas_1.emailSchema.max(254).nullable().default(null),
    phone: schemas_1.phoneSchema.nullable().default(null),
    nationalId: zod_1.z
        .string()
        .trim()
        .regex(/^(\d{10}|\d{13}|\d{17})$/, 'NID must be 10, 13, or 17 digits.')
        .nullable()
        .default(null),
    dateOfBirth: zod_1.z.iso.date('Enter a date in the form 1995-03-26.').nullable().default(null),
    gender: optionalText(40),
    city: optionalText(80),
    currentEmployer: optionalText(160),
    currentTitle: optionalText(160),
    linkedinUrl: optionalText(300),
    totalExperienceYears: zod_1.z.number().min(0).max(60).nullable().default(null),
    highestEducation: zod_1.z.enum(enums_1.EDUCATION_LEVELS).nullable().default(null),
    skills: zod_1.z.array(zod_1.z.string().trim().min(1).max(60)).max(30).default([]),
    certifications: zod_1.z.array(zod_1.z.string().trim().min(1).max(60)).max(30).default([]),
    languages: zod_1.z
        .array(zod_1.z.object({ language: zod_1.z.string().trim().min(1).max(60), proficiency: zod_1.z.enum(enums_1.LANGUAGE_PROFICIENCIES) }))
        .max(10)
        .default([]),
    /** Whole taka a month, as typed; stored in minor units (non-negotiable #7). */
    expectedSalary: zod_1.z.number().int('Enter a valid amount.').positive('Enter a valid amount.').max(999_999_999).nullable().default(null),
    noticePeriodDays: zod_1.z.number().int().min(0).max(365).nullable().default(null),
    isWillingToRelocate: zod_1.z.boolean().nullable().default(null),
    jobId: schemas_1.uuidSchema.nullable().default(null),
    /** S31 (ADMIN-08): any source the HR Lead keeps visible; the server checks it against the list. */
    source: zod_1.z.string().trim().regex(/^[a-z][a-z0-9_]{1,59}$/, 'Pick a source from the list.').default(exports.WALK_IN_SOURCE),
    /** S25 Q6: with no job, the pool the person goes into (null = General CVs). Ignored with a job. */
    poolId: schemas_1.uuidSchema.nullable().default(null),
    /** D15: consent is captured at intake, by whoever took the details. */
    hasConsent: zod_1.z.literal(true, 'Confirm the candidate agreed to us keeping their details.'),
})
    .refine((value) => value.email !== null || value.phone !== null, {
    path: ['phone'],
    message: 'Give an email or a mobile number, so we can recognise this person next time.',
});
// --- §L14 reapply waiting period (S14 Q4) --------------------------------------------
exports.DEFAULT_REAPPLY_AFTER_MONTHS = 6;
exports.MAX_REAPPLY_AFTER_MONTHS = 24;
/** The career site's words when someone applies again too soon. Never says "rejected". */
function reapplyBlockedMessage(appliedOn, allowedFrom) {
    return `You applied for this job on ${appliedOn}. You can apply for it again from ${allowedFrom}.`;
}
//# sourceMappingURL=candidates.js.map